import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface SourceComboboxProps {
  sources: string[];
  isLoading?: boolean;
  value: string | undefined;
  onValueChange: (source: string | undefined) => void;
  placeholder?: string;
  className?: string;
}

export function SourceCombobox({
  sources,
  isLoading,
  value,
  onValueChange,
  placeholder = "All sources",
  className,
}: SourceComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const displayValue = value ?? "";

  const filteredSources = useMemo(() => {
    if (!search.trim()) return sources;
    const q = search.trim().toLowerCase();
    return sources.filter((s) => s.toLowerCase().includes(q));
  }, [sources, search]);

  const handleSelect = (selected: string) => {
    const next =
      selected === "__all__"
        ? undefined
        : selected === value
          ? undefined
          : selected;
    onValueChange(next);
    setSearch("");
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange(undefined);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("min-w-[200px] justify-between font-normal", className)}
        >
          <span className="truncate">{displayValue || placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {value && (
              <X
                className="h-4 w-4 opacity-70 hover:opacity-100"
                aria-label="Clear source"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search sources..."
            className="h-9"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>
              {isLoading ? "Loading..." : "No source found."}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect("__all__")}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0",
                  )}
                />
                All sources
              </CommandItem>
              {filteredSources.map((source) => (
                <CommandItem
                  key={source}
                  value={source}
                  onSelect={() => handleSelect(source)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === source ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {source}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
